/**
 * MapCanvas — the center column of the editor. Renders the map document's group
 * geometries on a real Leaflet map (via react-leaflet), each area filled with
 * its group's color + fill_opacity, honoring per-group visibility.
 *
 * Geometry sources per area_type:
 *   - radius : drawn client-side as an L.Circle from params.center [lat,lon] +
 *              params.radius_mi (converted mi → meters). The contract stores
 *              radius as params, not geometry, so we render it here.
 *   - drawn, and zip/city/county/state/tract (census-backed): all rendered from
 *              the area's GeoJSON `geometry` field. For census-backed areas this
 *              is populated server-side (mapsService.saveMap →
 *              resolveCensusAreaGeometry resolves it from the boundary cache and
 *              stores it in group_areas.geometry), not client-supplied — so a
 *              newly-added county/city/ZIP/state/tract area has no polygon to
 *              draw until the next autosave round-trip fills it in. That's
 *              expected: the group-card chip/legend/export still reflects it
 *              immediately, only the canvas polygon lags by one save cycle.
 *
 * `extraFeatures` is an escape hatch for GeoJSON features fetched outside the
 * saved document (e.g. a future boundary-geometry endpoint, or Porter results
 * previewed on the map) keyed by group id.
 *
 * Drawing:
 *   - Draw area / Rectangle are powered by Leaflet-Geoman
 *     (@geoman-io/leaflet-geoman-free), which attaches to the raw Leaflet map
 *     instance (map.pm) — there is no React wrapper for Geoman, so we enable its
 *     draw modes imperatively from a child that holds useMap(). MapCanvas forwards
 *     a ref exposing drawPolygon()/drawRectangle()/drawCircle() so Editor's
 *     toolbar buttons can start a mode. When the user finishes a polygon/rectangle,
 *     Geoman fires `pm:create`; we convert that layer to
 *     { area_type: 'drawn', geometry: <GeoJSON Polygon> } (identical shape to a
 *     census/Porter 'drawn' area, just canvas-sourced) and hand it back via
 *     onShapeDrawn, then remove Geoman's temporary layer (the shape re-renders
 *     from the saved document, so leaving Geoman's copy would double it).
 *   - Radius does NOT use Geoman's free-hand circle drag (imprecise — you can't
 *     eyeball "exactly 10 miles"). Instead drawCircle() arms a one-shot map-click
 *     capture: the next click places the center point and opens a small React
 *     popup (RadiusEntryPopup) anchored to that point via
 *     map.latLngToContainerPoint(), asking for the exact radius in miles. On Add
 *     we build { area_type: 'radius', params: { center:[lat,lon], radius_mi } } and
 *     send it through the SAME onShapeDrawn path; on Cancel we discard. This is the
 *     approved flow in mockups/03-editor-shape-labels.html (.radius-entry-popup).
 *
 * Shape labels: radius/drawn groups also render a small on-canvas pill
 * (.shape-label) at the circle center / polygon centroid showing size + population,
 * plus a matching line on the group card. The pill is positioned every render and
 * on every Leaflet move/zoom via map.latLngToContainerPoint() so it tracks its
 * shape as the map pans/zooms (it is NOT fixed to the screen). The metrics
 * themselves come from Editor's useShapeMetrics hook (passed in as shapeMetrics).
 *
 * Click-to-select (approved mockups/04-editor-shape-selection.html): clicking a
 * rendered shape selects it (Editor owns `selectedArea` — lifted so the owning
 * GroupCard can mirror the same is-selected outline) and opens a small action
 * popup (ShapeActionPopup) anchored to it: radius areas get "Edit radius"
 * (reopens RadiusEntryPopup pre-filled, in edit mode), drawn areas get "Reshape"
 * (enables Geoman's native vertex-drag edit on that specific Leaflet layer, kept
 * in a ref map keyed by area id — see geoLayerRefsRef), census-backed areas get
 * neither (their boundary isn't hand-drawn). Every type gets Delete, which calls
 * up to Editor's onDeleteArea (confirms there, so the sidebar chip's remove
 * button gets the same confirmation for free). Only one shape is selected at a
 * time; clicking empty map space deselects (SelectionController below) unless a
 * popup/reshape/radius-edit is actively open, which requires its own explicit
 * exit so a stray click can't silently discard it. Each shape's click handler
 * stops propagation, so the topmost-rendered shape wins when shapes overlap,
 * rather than every shape under the cursor firing — true within a single
 * renderer: Canvas's own hit-testing (Leaflet's Canvas._onClick) walks its
 * draw-order list and keeps overwriting the match, so the LAST-drawn (topmost)
 * layer under the point wins, same effective rule as SVG's DOM stacking order.
 * ACROSS the two renderers below, stacking is instead pinned by pane (see
 * DRAWN_PANE_NAME) rather than left to whichever renderer's container
 * happened to be created first.
 *
 * Rendering: census-backed/radius shapes use Leaflet's Canvas renderer
 * (`preferCanvas` on MapContainer) instead of one SVG `<path>` DOM node per
 * area — very large groups (e.g. a whole state's ZIP set) could otherwise
 * still crash the tab on DOM node count alone even after server-side geometry
 * simplification (mapsService's SIMPLIFY_ABOVE_AREA_COUNT) shrank the byte
 * size. `drawn`-type areas are kept on an explicit SVG renderer (drawnRenderer
 * below) instead — they're always few (hand-drawn one at a time), and Geoman's
 * vertex-drag reshape (startReshape/finishReshape) is written against Leaflet
 * Path layers in a way that's only been exercised against SVG. That renderer
 * is pinned to its own pane (DRAWN_PANE_NAME, z-index just above the default
 * overlayPane the Canvas renderer uses) so drawn shapes always stack above
 * census/radius shapes, deterministically, regardless of add order.
 *
 * Basemap tiles use Carto/OSM public tiles selected by the map's `basemap`
 * value. These are free, attribution-required tile sources — fine for an
 * internal tool; revisit for heavy production volume.
 */

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MapContainer, TileLayer, Circle, GeoJSON, Pane, ZoomControl, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { Pencil, Move, Trash2, Check } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
// Side-effect import: registers L.PM on the Leaflet map instance (map.pm). Its
// CSS styles Geoman's draw cursors/handles/tooltips. Must load before any
// map.pm.* call runs (DrawController below).
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import { formatNumber, areaLabel } from '../utils/format';
import { polygonCentroid, polygonAreaSqMi, formatSqMi } from '../utils/geometry';
import './MapCanvas.css';

const MILES_TO_METERS = 1609.344;

// Pre-filled radius (miles) in the precise-radius entry popup. Matches the
// mock-up's default value of 10 — a common ad-targeting radius, and a sensible
// starting point the user overwrites as needed.
const DEFAULT_RADIUS_MI = 10;

// A batch of NEW census/drawn shapes at or below this size mounts in one go —
// chunking exists for genuinely large additions (a whole-state group), not
// everyday edits (adding one drawn shape to an already-large map). Above it,
// shapes are revealed RENDER_BATCH_SIZE at a time across animation frames
// instead of all in one React commit, so the browser can actually paint
// between batches — mounting 1000+ Leaflet layers synchronously is real
// main-thread work a loading spinner can't paint over (same thread).
const PROGRESSIVE_RENDER_THRESHOLD = 300;
const RENDER_BATCH_SIZE = 150;

// A single stable reference for callers that don't pass extraFeatures — a
// `= {}` default parameter value is a FRESH object on every call, and
// extraFeatures sits in the flatten useMemo's dependency array below, so a
// churning default would recompute geojsonLayers (and reset the progressive
// reveal below, which depends on geojsonLayers' identity) on every render,
// including the reveal's own per-frame re-renders. Confirmed empirically —
// without this, the reveal's progress total appeared to shrink/reset instead
// of counting down accurately.
const EMPTY_EXTRA_FEATURES = {};

// Same rationale as EMPTY_EXTRA_FEATURES above, for shapeMetrics — it also
// sits in the labelItems useMemo's dependency array, so a bare `= {}` default
// would churn on every render for any caller that omits the prop.
const EMPTY_SHAPE_METRICS = {};

// Dedicated pane for drawn-type shapes' SVG renderer, stacked just above the
// default overlayPane (zIndex 400, where the Canvas renderer for
// census/radius shapes lives). Without this, cross-renderer stacking would be
// decided by which renderer's DOM container got created first (see header
// comment) — an accident of add order, not a real rule. Pinning drawn shapes
// to a fixed higher pane makes "drawn shapes are always visible/clickable
// above census/radius shapes" a deterministic rule instead.
const DRAWN_PANE_NAME = 'drawn-shapes-pane';
const DRAWN_PANE_Z_INDEX = 401;

// Public basemap tile URLs keyed by the document's basemap enum
// (voyager|streets|light|dark). "streets" maps to OSM standard; the rest to
// Carto's free basemaps. Attribution is required and rendered by Leaflet.
const BASEMAPS = {
  voyager: {
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
  },
  streets: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
  light: {
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
  },
  dark: {
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
  },
};

// Continental-US default view when a map has no drawable geometry to fit.
const US_CENTER = [39.5, -98.35];
const US_ZOOM = 4;

/**
 * Anchor lat/lon for an area — where its shape label / selection popup /
 * "Done reshaping" button attaches. Radius uses its center; every other type
 * (drawn, and census-backed zip/city/county/state/tract once geometry has
 * resolved) uses its polygon centroid. Returns null if neither is available
 * yet (e.g. a just-added census area before the next autosave round-trip).
 *
 * @param {object} area
 * @returns {[number, number]|null}
 */
function anchorLatLngOf(area) {
  if (area.area_type === 'radius' && area.params?.center) return area.params.center;
  if (area.geometry && area.geometry.type) return polygonCentroid(area.geometry);
  return null;
}

/**
 * FitBounds — imperatively fits the Leaflet view to all drawable geometry each
 * time it changes. A child of MapContainer so it can call useMap().
 */
function FitBounds({ bounds }) {
  const map = useMap();
  React.useEffect(() => {
    if (bounds && bounds.length === 2) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }
  }, [map, bounds]);
  return null;
}

/**
 * DrawController — a headless child of MapContainer that owns the Geoman
 * integration (polygon/rectangle) AND the precise-radius click capture. It lives
 * here (not in MapCanvas) because both need the raw Leaflet map, which is only
 * reachable via useMap() from inside MapContainer.
 *
 * Responsibilities:
 *   - Configure Geoman's global options once (hide its built-in toolbar; we
 *     drive modes from the app's own toolbar buttons).
 *   - Publish start-mode triggers (polygon/rectangle/radius) up through the
 *     `controlsRef` handle so MapCanvas's forwarded ref can call them.
 *   - Handle Geoman's `pm:create` for polygon/rectangle: convert the finished
 *     layer to a 'drawn' area and invoke onShapeDrawn, then remove Geoman's own
 *     layer so the shape isn't drawn twice.
 *   - Radius: drawCircle() arms a one-shot `click` handler on the map. The next
 *     click reports the center point up via onRadiusCenter({ latlng, point }) so
 *     MapCanvas can open the radius-entry popup; the actual area is built later,
 *     when the user confirms the miles in that popup (see MapCanvas.confirmRadius).
 *
 * onShapeDrawn receives one argument: { kind: 'drawn'|'radius', area } where
 * `area` is a partial area object (area_type + geometry OR params); the Editor
 * fills in id/label/sort_order and wraps it in a group. For radius, only the
 * polygon/rectangle path calls onShapeDrawn directly here — the radius path routes
 * through onRadiusCenter → the popup → MapCanvas.
 *
 * @param {object} props
 * @param {{current: object|null}} props.controlsRef - published trigger handle
 * @param {(payload: {kind:'drawn', area: object}) => void} props.onShapeDrawn
 * @param {(placement: {latlng: object, point: {x:number,y:number}}) => void} props.onRadiusCenter
 *   - called with the clicked center when the radius tool captures a point.
 */
function DrawController({ controlsRef, onShapeDrawn, onRadiusCenter }) {
  const map = useMap();
  // Keep the latest callbacks without re-binding map events each render.
  const onShapeDrawnRef = useRef(onShapeDrawn);
  onShapeDrawnRef.current = onShapeDrawn;
  const onRadiusCenterRef = useRef(onRadiusCenter);
  onRadiusCenterRef.current = onRadiusCenter;

  React.useEffect(() => {
    if (!map || !map.pm) return;

    // We supply our own toolbar (the .map-float-tl buttons), so keep Geoman's
    // built-in control hidden. Snapping/self-intersection guards are Geoman
    // defaults — left on; they only improve drawn polygons.
    map.pm.setGlobalOptions({ snappable: true });

    // pm:create fires once a polygon/rectangle is finished. e.shape is
    // 'Polygon' | 'Rectangle'; e.layer is the Leaflet layer Geoman just added.
    // (Circles are handled by the click-to-place flow, not Geoman — see below.)
    const handleCreate = (e) => {
      const cb = onShapeDrawnRef.current;
      const layer = e.layer;
      try {
        // Polygon / Rectangle: capture the GeoJSON geometry. toGeoJSON() yields a
        // Feature; we keep just .geometry (a Polygon), matching how census/Porter
        // 'drawn' areas store geometry. Rectangles serialize as Polygons.
        const geojson = layer.toGeoJSON();
        const geometry = geojson && geojson.geometry ? geojson.geometry : null;
        if (cb && geometry) {
          cb({ kind: 'drawn', area: { area_type: 'drawn', geometry, params: {} } });
        }
      } finally {
        // Remove Geoman's temporary layer regardless — the shape now lives in the
        // map document and re-renders from there. Leaving it would duplicate the
        // shape (and Geoman's copy isn't styled with the group color).
        if (map.hasLayer(layer)) map.removeLayer(layer);
      }
    };

    map.on('pm:create', handleCreate);

    // ── Precise-radius click capture ────────────────────────────────────────
    // A one-shot map-click handler, installed only while the radius tool is armed.
    // On click it reports the center up (latlng + container-pixel point) and
    // disarms itself; MapCanvas then shows the radius-entry popup at that point.
    const handleRadiusClick = (e) => {
      map.off('click', handleRadiusClick);
      // Restore the normal cursor once a point is placed.
      map.getContainer().style.cursor = '';
      const cb = onRadiusCenterRef.current;
      if (cb) cb({ latlng: e.latlng, point: e.containerPoint });
    };

    const armRadius = () => {
      // Cancel any in-progress Geoman draw first so modes don't stack.
      if (map.pm) map.pm.disableDraw();
      // Re-arm cleanly: drop any prior handler before adding one.
      map.off('click', handleRadiusClick);
      map.on('click', handleRadiusClick);
      // Crosshair cursor signals "click to place the center point".
      map.getContainer().style.cursor = 'crosshair';
    };

    const disarmRadius = () => {
      map.off('click', handleRadiusClick);
      map.getContainer().style.cursor = '';
    };

    // Expose the start-mode triggers. Polygon/Rectangle enable a one-shot Geoman
    // mode (continueDrawing:false → disables itself after a single shape).
    if (controlsRef) {
      controlsRef.current = {
        drawPolygon: () => {
          disarmRadius();
          map.pm.enableDraw('Polygon', { continueDrawing: false });
        },
        drawRectangle: () => {
          disarmRadius();
          map.pm.enableDraw('Rectangle', { continueDrawing: false });
        },
        // Radius no longer uses Geoman — arm the click-to-place capture instead.
        drawCircle: armRadius,
        // Cancel any in-progress draw / radius arming (used on popup cancel,
        // unmount, or mode switch).
        cancel: () => {
          if (map.pm) map.pm.disableDraw();
          disarmRadius();
        },
      };
    }

    return () => {
      map.off('pm:create', handleCreate);
      // Stop any active draw mode / radius arming so nothing outlives the map.
      if (map.pm) map.pm.disableDraw();
      disarmRadius();
      if (controlsRef) controlsRef.current = null;
    };
  }, [map, controlsRef]);

  return null;
}

/**
 * SelectionController — a headless child of MapContainer that deselects the
 * currently-selected shape when the user clicks empty map space. Each shape's
 * own click handler calls L.DomEvent.stopPropagation, so this only fires for
 * clicks that didn't land on a shape.
 *
 * `suppressed` is true while a popup that requires an explicit exit is open
 * (the precise-radius create popup, the radius-edit popup, or an active
 * reshape) — a stray background click during one of those should not silently
 * discard it, matching those popups' existing explicit-only dismissal.
 *
 * @param {object} props
 * @param {boolean} props.suppressed
 * @param {() => void} props.onDeselect
 */
function SelectionController({ suppressed, onDeselect }) {
  const map = useMap();
  const suppressedRef = useRef(suppressed);
  suppressedRef.current = suppressed;
  const onDeselectRef = useRef(onDeselect);
  onDeselectRef.current = onDeselect;

  useEffect(() => {
    if (!map) return;
    const handleClick = () => {
      if (suppressedRef.current) return;
      onDeselectRef.current?.();
    };
    map.on('click', handleClick);
    return () => map.off('click', handleClick);
  }, [map]);

  return null;
}

/**
 * ShapeLabels — renders the on-canvas size/population pills (.shape-label) for
 * radius/drawn groups, anchored to each shape's center (radius) or centroid
 * (drawn). A child of MapContainer so it can project lat/lon → container pixels
 * via map.latLngToContainerPoint(). It subscribes to Leaflet move/zoom/resize
 * (useMapEvents) and bumps a tick to re-project on every viewport change, so the
 * labels track their shapes as the map pans/zooms rather than sticking to fixed
 * screen coordinates. pointer-events:none (in CSS) keeps them from blocking the
 * shapes underneath.
 *
 * @param {object} props
 * @param {Array<{key:string, latlng:[number,number], text:string, pop:string|null}>} props.items
 *   - one entry per labeled shape: its anchor [lat,lon], the size text, and the
 *     already-formatted population string (or null while loading / on error).
 */
function ShapeLabels({ items }) {
  const map = useMap();
  // A monotonically-increasing tick forces a re-render (and thus re-projection of
  // every label) whenever the map view changes. `move` covers pan + inertia;
  // `zoom` covers zoom animation frames; `resize` covers panel/window resizes.
  const [, setTick] = useState(0);
  useMapEvents({
    move: () => setTick((t) => t + 1),
    zoom: () => setTick((t) => t + 1),
    resize: () => setTick((t) => t + 1),
  });

  if (!items || items.length === 0) return null;

  return (
    <>
      {items.map((item) => {
        // Project the shape's geographic anchor to a pixel offset in the map
        // container. The CSS translate(-50%,-50%) then centers the pill on it.
        const pt = map.latLngToContainerPoint(item.latlng);
        return (
          <div
            key={item.key}
            className="shape-label"
            style={{ left: `${pt.x}px`, top: `${pt.y}px` }}
          >
            {item.text}
            {item.pop != null && (
              <>
                <span className="dot">·</span>
                <span className="pop">{item.pop}</span>
              </>
            )}
          </div>
        );
      })}
    </>
  );
}

/**
 * SelectionOverlay — renders the click-to-select action popup (or, while
 * reshaping, the "Done reshaping" button) anchored to the selected shape. A
 * child of MapContainer so it can project lat/lon → container pixels and
 * re-project on pan/zoom (same tick pattern as ShapeLabels).
 *
 * Exactly one of `popup`/`reshaping` is meaningful at a time: while `reshaping`
 * is set, the popup is hidden (Geoman's vertex handles are the interaction) and
 * only the Done button shows; otherwise the popup shows (if a shape is selected).
 *
 * @param {object} props
 * @param {{latlng:[number,number], title:string, context:string|null, color:string,
 *   canEditRadius:boolean, canReshape:boolean, onEditRadius:(point:{x:number,y:number})=>void,
 *   onReshape:()=>void, onDelete:()=>void}|null} props.popup
 * @param {{latlng:[number,number]}|null} props.reshaping - live-tracked anchor
 *   while a shape is in vertex-edit mode
 * @param {() => void} props.onDoneReshaping
 */
function SelectionOverlay({ popup, reshaping, onDoneReshaping }) {
  const map = useMap();
  const [, setTick] = useState(0);
  useMapEvents({
    move: () => setTick((t) => t + 1),
    zoom: () => setTick((t) => t + 1),
    resize: () => setTick((t) => t + 1),
  });

  // This overlay renders inside the Leaflet container (needed for useMap()'s
  // projection), so its buttons are plain HTML sitting inside the map's own
  // click-handling zone. Leaflet's container-level click listener doesn't find
  // a registered layer for a click on a <button>, falls back to treating it as
  // a "map click", and fires the map's own click listeners — including
  // SelectionController's deselect handler, which unmounts this popup out from
  // under the click before/as it reaches the button's onClick. Same standard
  // fix Leaflet's own built-in controls (e.g. the zoom control) use for
  // embedding HTML controls inside the map: stop the click (and wheel, so
  // scrolling inside the popup doesn't zoom the map) from reaching Leaflet at
  // the DOM level, via a ref callback so it's (re)applied whenever this
  // conditionally-rendered root node (re)mounts.
  const stopMapInterference = (node) => {
    if (!node) return;
    L.DomEvent.disableClickPropagation(node);
    L.DomEvent.disableScrollPropagation(node);
  };

  if (reshaping) {
    const pt = map.latLngToContainerPoint(reshaping.latlng);
    return (
      <div
        className="done-reshaping"
        style={{ left: `${pt.x}px`, top: `${pt.y}px` }}
        ref={stopMapInterference}
      >
        <button type="button" className="btn btn-primary btn-sm" onClick={onDoneReshaping}>
          <Check size={13} /> Done reshaping
        </button>
      </div>
    );
  }

  if (!popup) return null;
  const pt = map.latLngToContainerPoint(popup.latlng);
  return (
    <div
      className="shape-action-popup"
      style={{ left: `${pt.x}px`, top: `${pt.y}px` }}
      role="dialog"
      aria-label={popup.title}
      ref={stopMapInterference}
    >
      <div className="sap-head">
        <span className="swatch" style={{ background: popup.color }} />
        <div className="sap-head-text">
          <div className="sap-title">{popup.title}</div>
          {popup.context && <div className="sap-context">{popup.context}</div>}
        </div>
      </div>
      {popup.canEditRadius && (
        <button type="button" className="sap-action" onClick={() => popup.onEditRadius(pt)}>
          <Pencil size={14} /> Edit radius
        </button>
      )}
      {popup.canReshape && (
        <button type="button" className="sap-action" onClick={popup.onReshape}>
          <Move size={14} /> Reshape
        </button>
      )}
      <button type="button" className="sap-action danger" onClick={popup.onDelete}>
        <Trash2 size={14} /> Delete
      </button>
    </div>
  );
}

/**
 * RadiusEntryPopup — the precise-radius entry UI shown after the user clicks the
 * map to place a center point (approved mock-up .radius-entry-popup). Positioned
 * at the click's container pixel via absolute left/top. A numeric miles input
 * (pre-filled with a sensible default), Cancel, and Add. Add validates a positive
 * finite number and calls onAdd(miles); Cancel calls onCancel. Enter submits,
 * Escape cancels. Rendered outside the Leaflet container (a sibling in
 * .map-canvas) so it isn't affected by map panning while open — the center point
 * is already fixed once placed.
 *
 * Also reused (with heading/submitLabel overridden) for editing an existing
 * radius area from the click-to-select popup — same fields/validation, just a
 * different label and an onAdd that patches the area's params instead of
 * creating a new one.
 *
 * @param {object} props
 * @param {{x:number, y:number}} props.point - container-pixel position of the click
 * @param {number} props.defaultMiles - pre-filled radius value
 * @param {(miles:number) => void} props.onAdd
 * @param {() => void} props.onCancel
 * @param {string} [props.heading] - defaults to the create-flow's copy
 * @param {string} [props.submitLabel] - defaults to "Add"
 */
function RadiusEntryPopup({
  point,
  defaultMiles,
  onAdd,
  onCancel,
  heading = 'Set radius from this point',
  submitLabel = 'Add',
}) {
  const [value, setValue] = useState(String(defaultMiles));
  const inputRef = useRef(null);

  // Focus + select the input on open so the user can immediately type a value.
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const submit = () => {
    const miles = parseFloat(value);
    if (Number.isFinite(miles) && miles > 0) onAdd(miles);
  };

  // Nudge the popup left/up if the click was near the right/bottom edge would be
  // ideal, but a fixed offset keeps it simple and matches the mock-up placement:
  // anchored just below-right of the click point.
  const style = { left: `${point.x + 8}px`, top: `${point.y + 8}px` };

  return (
    <div
      className="radius-entry-popup"
      style={style}
      role="dialog"
      aria-label={heading}
    >
      <div className="re-label">{heading}</div>
      <div className="re-row">
        <input
          ref={inputRef}
          className="input"
          type="number"
          min="0"
          step="0.5"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape') onCancel();
          }}
          style={{ width: '70px' }}
        />
        <span className="re-unit">miles</span>
      </div>
      <div className="re-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={submit}>
          {submitLabel}
        </button>
      </div>
    </div>
  );
}

/**
 * MapCanvas — forwardRef so Editor can trigger draw modes imperatively. The
 * exposed handle mirrors DrawController's triggers (drawPolygon/drawRectangle/
 * drawCircle/cancel); calls no-op safely before the map has mounted. Starting
 * any draw mode also exits an in-progress reshape (committing it — Geoman has
 * already live-mutated that layer's coordinates by the time the user switches
 * tools, so silently discarding would look like the shape reverted) and closes
 * an open radius-edit popup (nothing to commit there; Cancel is its only
 * meaningful exit).
 *
 * @param {object} props
 * @param {object} props.mapDoc - the map document (groups/areas/basemap).
 * @param {object} [props.extraFeatures] - GeoJSON features keyed by group id.
 * @param {object} [props.shapeMetrics] - size/population per group id (from
 *   useShapeMetrics); drives the on-canvas .shape-label pills.
 * @param {(payload: {kind:'drawn'|'radius', area: object}) => void} [props.onShapeDrawn]
 *   - called when the user finishes drawing a shape on the canvas.
 * @param {{groupId:string, areaId:string}|null} [props.selectedArea] - the
 *   shape currently selected (click-to-select addendum); owned by Editor so
 *   the matching GroupCard can mirror the same is-selected state.
 * @param {(payload:{groupId:string,areaId:string}|null) => void} [props.onSelectArea]
 *   - called with the clicked shape's ids, or null to deselect.
 * @param {(groupId:string, areaId:string) => void} [props.onDeleteArea]
 *   - called when the popup's Delete is clicked (Editor's removeArea, which
 *     owns the confirm() prompt).
 * @param {(groupId:string, areaId:string, radiusMi:number) => void} [props.onEditRadius]
 *   - called when the radius-edit popup is submitted with a new value.
 * @param {(groupId:string, areaId:string, geometry:object) => void} [props.onReshapeCommit]
 *   - called once, when "Done reshaping" commits the layer's final geometry.
 */
const MapCanvas = forwardRef(function MapCanvas(
  {
    mapDoc,
    extraFeatures = EMPTY_EXTRA_FEATURES,
    shapeMetrics = EMPTY_SHAPE_METRICS,
    onShapeDrawn,
    selectedArea = null,
    onSelectArea,
    onDeleteArea,
    onEditRadius,
    onReshapeCommit,
  },
  ref
) {
  const basemapKey = mapDoc?.basemap && BASEMAPS[mapDoc.basemap] ? mapDoc.basemap : 'voyager';
  const tiles = BASEMAPS[basemapKey];

  // Bridge: DrawController (inside MapContainer) publishes its triggers here; the
  // forwarded ref proxies to whatever's current, so it works once the map mounts.
  const controlsRef = useRef(null);

  // Pending precise-radius entry: set when the radius tool captures a center
  // click, cleared on Add/Cancel. Holds the click's container-pixel point (to
  // position the popup) and its latlng (to build the area on confirm).
  // { point: {x,y}, latlng: {lat,lng} } | null.
  const [pendingRadius, setPendingRadius] = useState(null);

  // Radius-edit popup (click-to-select addendum): same shape as pendingRadius
  // plus the ids of the area being edited and its current value to pre-fill.
  // { groupId, areaId, point: {x,y}, currentMiles } | null.
  const [editingRadius, setEditingRadius] = useState(null);

  // Active reshape (Geoman vertex-edit on a drawn area's own Leaflet layer).
  // liveGeometry updates on each vertex drag so the on-canvas size label tracks
  // the shape as it's reshaped, without writing into mapDoc on every frame (that
  // would replace the GeoJSON layer's `data` prop mid-drag and kill the active
  // edit session — see geoLayerRefsRef below).
  // { groupId, areaId, layer: L.Layer, liveGeometry: object|null } | null.
  const [reshaping, setReshaping] = useState(null);

  // The actual Leaflet layer instance backing each 'drawn'-area <GeoJSON>,
  // keyed by area id — populated via each element's ref callback. Needed to
  // call layer.pm.enable() on the SPECIFIC layer Reshape was clicked for
  // (react-leaflet's <GeoJSON> ref resolves to the L.GeoJSON FeatureGroup, not
  // the single Polygon inside it, hence .getLayers()[0] where it's used below).
  const geoLayerRefsRef = useRef(new Map());

  // Shared SVG renderer for 'drawn'-type shapes only (see header comment) — a
  // single instance so every drawn layer renders into the same SVG root rather
  // than each getting its own. Everything else falls through to the
  // MapContainer's preferCanvas default.
  const drawnRendererRef = useRef(null);
  if (!drawnRendererRef.current) drawnRendererRef.current = L.svg({ pane: DRAWN_PANE_NAME });

  // Latest prop values without re-creating handlers that reference them each
  // render (mirrors the existing onShapeDrawnRef pattern below).
  const onShapeDrawnRef = useRef(onShapeDrawn);
  onShapeDrawnRef.current = onShapeDrawn;
  const onSelectAreaRef = useRef(onSelectArea);
  onSelectAreaRef.current = onSelectArea;
  const onDeleteAreaRef = useRef(onDeleteArea);
  onDeleteAreaRef.current = onDeleteArea;
  const onEditRadiusRef = useRef(onEditRadius);
  onEditRadiusRef.current = onEditRadius;
  const onReshapeCommitRef = useRef(onReshapeCommit);
  onReshapeCommitRef.current = onReshapeCommit;

  // Called by DrawController when the radius tool captures a center point.
  const handleRadiusCenter = useCallback((placement) => {
    setPendingRadius(placement);
  }, []);

  // Confirm the radius popup: build the radius area (same shape the old drag flow
  // produced) and route it through onShapeDrawn, then dismiss the popup.
  const confirmRadius = useCallback((miles) => {
    const placement = pendingRadius;
    setPendingRadius(null);
    const cb = onShapeDrawnRef.current;
    if (!placement || !cb) return;
    cb({
      kind: 'radius',
      area: {
        area_type: 'radius',
        geometry: {},
        params: {
          center: [placement.latlng.lat, placement.latlng.lng],
          // Round to 2 decimals for clean labels/exports; the user typed the value
          // so this only guards against a stray long decimal.
          radius_mi: Math.round(miles * 100) / 100,
        },
      },
    });
  }, [pendingRadius]);

  // Cancel the radius popup without creating an area.
  const cancelRadius = useCallback(() => {
    setPendingRadius(null);
    controlsRef.current?.cancel();
  }, []);

  // ─── Click-to-select: radius editing ──────────────────────────────────────
  const startEditRadius = useCallback((groupId, areaId, point, currentMiles) => {
    setEditingRadius({ groupId, areaId, point, currentMiles });
  }, []);

  const confirmEditRadius = useCallback(
    (miles) => {
      const ctx = editingRadius;
      setEditingRadius(null);
      if (!ctx) return;
      onEditRadiusRef.current?.(ctx.groupId, ctx.areaId, Math.round(miles * 100) / 100);
    },
    [editingRadius]
  );

  const cancelEditRadius = useCallback(() => setEditingRadius(null), []);

  // ─── Click-to-select: drawn-area reshaping ─────────────────────────────────
  // Enable Geoman's native vertex-drag edit on the specific layer backing this
  // area. handleLiveEdit re-reads the layer's current geometry on each vertex
  // change and stores it as `liveGeometry` — display-only (on-canvas size
  // label), never written to mapDoc until finishReshape commits.
  const startReshape = useCallback((groupId, areaId) => {
    const geoLayer = geoLayerRefsRef.current.get(areaId);
    const layer = geoLayer?.getLayers?.()[0];
    if (!layer || !layer.pm) return;

    const handleLiveEdit = () => {
      try {
        const gj = layer.toGeoJSON();
        if (gj?.geometry) {
          setReshaping((prev) => (prev && prev.areaId === areaId ? { ...prev, liveGeometry: gj.geometry } : prev));
        }
      } catch {
        // Mid-drag the layer can transiently have too few points to serialize;
        // just skip that frame's live update rather than throwing.
      }
    };
    layer.on('pm:markerdragend', handleLiveEdit);
    layer.on('pm:vertexadded', handleLiveEdit);
    layer.on('pm:vertexremoved', handleLiveEdit);
    layer._fpsLiveEditHandler = handleLiveEdit;

    layer.pm.enable({ allowSelfIntersection: false });
    setReshaping({ groupId, areaId, layer, liveGeometry: null });
  }, []);

  // Exit reshape mode, detach the Geoman listeners, and — if the layer is still
  // mounted — commit its final geometry up to Editor. A stable identity (the
  // setReshaping updater reads the current value itself) so it's safe to call
  // from useImperativeHandle and the unmount cleanup below without stale closures.
  const finishReshape = useCallback(() => {
    setReshaping((prev) => {
      if (!prev) return null;
      const { layer, groupId, areaId } = prev;
      if (layer) {
        try {
          if (layer._fpsLiveEditHandler) {
            layer.off('pm:markerdragend', layer._fpsLiveEditHandler);
            layer.off('pm:vertexadded', layer._fpsLiveEditHandler);
            layer.off('pm:vertexremoved', layer._fpsLiveEditHandler);
            delete layer._fpsLiveEditHandler;
          }
          if (layer.pm) layer.pm.disable();
          const gj = layer.toGeoJSON();
          if (gj?.geometry) onReshapeCommitRef.current?.(groupId, areaId, gj.geometry);
        } catch {
          // The layer may already be detached (e.g. this fires from the
          // unmount-cleanup effect after the map itself started tearing down).
          // Leave mapDoc's prior geometry in place rather than commit a
          // half-formed read.
        }
      }
      return null;
    });
  }, []);

  // Exiting a draw mode (or starting a new one) shouldn't leave a reshape or
  // radius-edit dangling — see the function's JSDoc above for why reshape
  // commits rather than discards.
  const exitSelectionInteractions = useCallback(() => {
    finishReshape();
    cancelEditRadius();
  }, [finishReshape, cancelEditRadius]);

  useImperativeHandle(
    ref,
    () => ({
      drawPolygon: () => {
        exitSelectionInteractions();
        controlsRef.current?.drawPolygon();
      },
      drawRectangle: () => {
        exitSelectionInteractions();
        controlsRef.current?.drawRectangle();
      },
      drawCircle: () => {
        exitSelectionInteractions();
        controlsRef.current?.drawCircle();
      },
      cancel: () => {
        exitSelectionInteractions();
        controlsRef.current?.cancel();
      },
    }),
    [exitSelectionInteractions]
  );

  // Commit (not discard) any active reshape if the canvas unmounts mid-edit —
  // Geoman has already mutated the layer's live coordinates by then.
  useEffect(() => () => finishReshape(), [finishReshape]);

  // While any of these is open, background clicks and other shapes' click
  // handlers are suppressed — each of these three has its own explicit exit
  // (Add/Cancel or Done reshaping), so a stray click shouldn't silently swap
  // the selection or discard it.
  const interactionLocked = Boolean(pendingRadius || editingRadius || reshaping);
  const interactionLockedRef = useRef(interactionLocked);
  interactionLockedRef.current = interactionLocked;

  // Stable per-area click handler, cached by area id so the `eventHandlers`
  // object passed to <Circle>/<GeoJSON> keeps the SAME reference across
  // unrelated re-renders (e.g. a population fetch resolving elsewhere).
  // @react-leaflet/core's useEventHandlers unbinds+rebinds on every
  // eventHandlers identity change — a literal `eventHandlers={{click: ...}}`
  // is a new object every render, so without this cache Leaflet would tear
  // down and re-add the click listener on every single MapCanvas re-render.
  // interactionLocked is read via the ref above so the cached handler never
  // closes over a stale value.
  const shapeClickHandlersRef = useRef(new Map());
  const getShapeClickHandlers = useCallback((groupId, areaId) => {
    const cached = shapeClickHandlersRef.current.get(areaId);
    if (cached) return cached;
    const handlers = {
      click: (e) => {
        // Stop native DOM propagation so a shape underneath an overlapping
        // one, and the map's own background-click deselect handler, don't
        // also see this click.
        L.DomEvent.stopPropagation(e);
        if (interactionLockedRef.current) return;
        onSelectAreaRef.current?.({ groupId, areaId });
      },
    };
    shapeClickHandlersRef.current.set(areaId, handlers);
    return handlers;
  }, []);

  // Flatten visible groups into drawable primitives, tagging each with its
  // group's style. Recomputed whenever the document or extra features change.
  const { circles, geojsonLayers, bounds } = useMemo(() => {
    const circlesOut = [];
    const geojsonOut = [];
    const latlngs = [];

    const groups = mapDoc?.groups || [];
    for (const group of groups) {
      if (group.is_visible === false) continue;
      const style = {
        color: group.color || '#2563EB',
        fillColor: group.color || '#2563EB',
        fillOpacity: typeof group.fill_opacity === 'number' ? group.fill_opacity : 0.45,
        weight: 2,
      };

      for (const area of group.areas || []) {
        if (area.area_type === 'radius' && area.params?.center && area.params?.radius_mi) {
          const [lat, lon] = area.params.center;
          const radiusM = area.params.radius_mi * MILES_TO_METERS;
          circlesOut.push({ key: area.id, groupId: group.id, center: [lat, lon], radius: radiusM, style });
          // Rough bounds contribution: the circle's bounding box corners.
          const dLat = radiusM / 111320;
          const dLon = radiusM / (111320 * Math.cos((lat * Math.PI) / 180) || 1);
          latlngs.push([lat - dLat, lon - dLon], [lat + dLat, lon + dLon]);
        } else if (area.geometry && area.geometry.type) {
          // 'drawn' areas carry client geometry; census-backed areas carry
          // server-resolved geometry once a save has round-tripped (see header).
          geojsonOut.push({
            key: area.id,
            groupId: group.id,
            geometry: area.geometry,
            style,
            isDrawn: area.area_type === 'drawn',
          });
          collectGeoJSONBounds(area.geometry, latlngs);
        }
      }

      // Any externally-fetched features attached to this group render the same
      // way census areas eventually will.
      const extras = extraFeatures[group.id];
      if (Array.isArray(extras)) {
        for (const feat of extras) {
          geojsonOut.push({
            key: `${group.id}-${feat.id || Math.random()}`,
            groupId: group.id,
            geometry: feat,
            style,
            isDrawn: false,
          });
          collectGeoJSONBounds(feat, latlngs);
        }
      }
    }

    // A running min/max, not Math.min(...arr)/Math.max(...arr) — spreading an
    // array as call arguments hits V8's ~131k argument ceiling (RangeError:
    // Maximum call stack size exceeded), which a large state-wide area set
    // (thousands of vertices even after server-side simplification) can
    // exceed. That throw during render (no error boundary in this app) is
    // what produced a blank white screen for a real large map in production.
    let computedBounds = null;
    if (latlngs.length > 0) {
      let minLat = Infinity;
      let minLon = Infinity;
      let maxLat = -Infinity;
      let maxLon = -Infinity;
      for (const [lat, lon] of latlngs) {
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;
      }
      computedBounds = [
        [minLat, minLon],
        [maxLat, maxLon],
      ];
    }

    return { circles: circlesOut, geojsonLayers: geojsonOut, bounds: computedBounds };
  }, [mapDoc, extraFeatures]);

  // ─── Progressive shape reveal ───────────────────────────────────────────────
  // Mounting hundreds/thousands of <GeoJSON> layers in one React commit is
  // real synchronous main-thread work (each one does Leaflet layer setup +
  // an initial Canvas draw) — confirmed as the actual cause of a production
  // freeze for whole-state area sets, not merely a missing-feedback issue a
  // spinner could paint over. revealedKeysRef tracks which area/feature keys
  // are allowed to render; it only grows, so shapes already on screen never
  // disappear when mapDoc changes for an unrelated reason (renaming a group,
  // tweaking a color) — only genuinely NEW keys get progressively revealed.
  const revealedKeysRef = useRef(new Set());
  const [revealVersion, setRevealVersion] = useState(0);
  const [renderProgress, setRenderProgress] = useState(null); // {done, total} | null

  useEffect(() => {
    const newKeys = geojsonLayers.map((g) => g.key).filter((k) => !revealedKeysRef.current.has(k));

    // Always start from a clean progress state: this effect can re-run mid-
    // reveal (e.g. renaming a group while a large batch is still being
    // batched in), and the previous run's cleanup only cancels its RAF loop —
    // it doesn't know whether THIS run will need the pill at all. Reset here;
    // the slow-path branch below immediately overrides it with the real
    // {done,total} if it decides it needs one.
    setRenderProgress(null);

    if (newKeys.length === 0) return undefined;

    if (newKeys.length <= PROGRESSIVE_RENDER_THRESHOLD) {
      // Small enough to mount in one commit — this is the common case
      // (loading a normal map, adding one drawn shape to an existing map).
      newKeys.forEach((k) => revealedKeysRef.current.add(k));
      setRevealVersion((v) => v + 1);
      return undefined;
    }

    let cancelled = false;
    let revealed = 0;
    setRenderProgress({ done: 0, total: newKeys.length });

    const revealNextBatch = () => {
      if (cancelled) return;
      const batch = newKeys.slice(revealed, revealed + RENDER_BATCH_SIZE);
      batch.forEach((k) => revealedKeysRef.current.add(k));
      revealed += batch.length;
      setRevealVersion((v) => v + 1);
      if (revealed < newKeys.length) {
        setRenderProgress({ done: revealed, total: newKeys.length });
        requestAnimationFrame(revealNextBatch);
      } else {
        setRenderProgress(null);
      }
    };
    requestAnimationFrame(revealNextBatch);

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- revealedKeysRef
    // is intentionally a ref (mutated, not reactive); geojsonLayers is the
    // only real trigger for "is there something new to reveal".
  }, [geojsonLayers]);

  // The only shapes actually mounted as <GeoJSON> — a subset of geojsonLayers
  // while a large batch is still being progressively revealed. revealVersion
  // isn't read in the body, but it's the reactive signal for revealedKeysRef's
  // (a ref, so non-reactive on its own) mutations — it must stay a dependency.
  const visibleGeojsonLayers = useMemo(
    () => geojsonLayers.filter((g) => revealedKeysRef.current.has(g.key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [geojsonLayers, revealVersion]
  );

  // Build the on-canvas label descriptors from the shape metrics + group state.
  // One label per visible radius/drawn group that has a computed size + anchor.
  // The population string is null while loading / on error (the pill then shows
  // just the size, consistent with the card's "Calculating…" state).
  //
  // While the group being reshaped is mid-drag, its size/anchor come from
  // reshaping.liveGeometry (recomputed client-side, cheap) instead of the
  // metrics cache — population intentionally does NOT live-update (it's a
  // network round-trip; it re-fetches once, after Done reshaping commits the
  // final geometry and the shape's metrics signature changes).
  const labelItems = useMemo(() => {
    const groups = mapDoc?.groups || [];
    const items = [];
    for (const group of groups) {
      if (group.is_visible === false) continue;
      const m = shapeMetrics[group.id];
      if (!m || !m.sizeLabel) continue;

      let anchor = m.centroid;
      let sizeText = m.sizeLabel.replace(/ radius$/, '');
      if (reshaping && reshaping.groupId === group.id && reshaping.liveGeometry) {
        const liveAnchor = polygonCentroid(reshaping.liveGeometry);
        if (liveAnchor) anchor = liveAnchor;
        sizeText = formatSqMi(polygonAreaSqMi(reshaping.liveGeometry));
      }
      // Anchor fallback: recompute a drawn centroid defensively if metrics
      // somehow lacked one (e.g. computed before geometry settled).
      if (!anchor) {
        const area = (group.areas || [])[0];
        if (area?.area_type === 'radius') anchor = area.params?.center || null;
        else if (area?.geometry) anchor = polygonCentroid(area.geometry);
      }
      if (!anchor) continue;
      items.push({
        key: group.id,
        latlng: anchor,
        // On-canvas pill uses the compact size form: "10 mi" / "12.4 sq mi".
        // sizeLabel is "10 mi radius" / "12.4 sq mi"; drop the trailing "radius"
        // so the pill matches the mock-up's "10 mi · 45,231 pop." exactly.
        text: sizeText,
        pop: m.loading || m.error || m.population == null
          ? null
          : `${formatNumber(m.population)} pop.`,
      });
    }
    return items;
  }, [mapDoc, shapeMetrics, reshaping]);

  // ─── Click-to-select: resolve the selected {groupId,areaId} to its actual
  // group/area objects, and build the popup/reshape descriptors SelectionOverlay
  // renders. Both derive from mapDoc, so an edit-radius/reshape commit (which
  // updates mapDoc) automatically refreshes what the still-open popup shows.
  const selectedInfo = useMemo(() => {
    if (!selectedArea) return null;
    const group = (mapDoc?.groups || []).find((g) => g.id === selectedArea.groupId);
    if (!group) return null;
    const area = (group.areas || []).find((a) => a.id === selectedArea.areaId);
    if (!area) return null;
    return { group, area };
  }, [mapDoc, selectedArea]);

  let overlayPopup = null;
  let overlayReshaping = null;
  if (selectedInfo && reshaping) {
    const liveAnchor = reshaping.liveGeometry ? polygonCentroid(reshaping.liveGeometry) : null;
    const anchor = liveAnchor || anchorLatLngOf(selectedInfo.area);
    if (anchor) overlayReshaping = { latlng: anchor };
  } else if (selectedInfo && !editingRadius) {
    // Hide the plain action popup while the radius-edit popup (a separate,
    // sibling-rendered RadiusEntryPopup) is open — otherwise both would show
    // at once, stacked on the same anchor.
    const { group, area } = selectedInfo;
    const anchor = anchorLatLngOf(area);
    if (anchor) {
      const isRadius = area.area_type === 'radius';
      const isDrawn = area.area_type === 'drawn';
      overlayPopup = {
        latlng: anchor,
        title: isRadius || isDrawn ? group.name || 'Group' : areaLabel(area),
        context: isRadius
          ? `${area.params?.radius_mi ?? '—'} mi from center point`
          : isDrawn
            ? shapeMetrics[group.id]?.sizeLabel || null
            : group.name || null,
        color: group.color || '#2563EB',
        canEditRadius: isRadius,
        canReshape: isDrawn,
        onEditRadius: (point) => startEditRadius(group.id, area.id, point, area.params?.radius_mi ?? DEFAULT_RADIUS_MI),
        onReshape: () => startReshape(group.id, area.id),
        onDelete: () => onDeleteAreaRef.current?.(group.id, area.id),
      };
    }
  }

  return (
    <div className="map-canvas-inner">
      <MapContainer
        className="leaflet-canvas"
        center={US_CENTER}
        zoom={US_ZOOM}
        scrollWheelZoom
        zoomControl={false}
        preferCanvas
      >
        {/* Default zoom control sits top-left, under the .map-float-tl draw
            toolbar (also top-left) — move it to top-right so neither covers
            the other. */}
        <ZoomControl position="topright" />
        {/* Registered before any layer references it by name (drawnRendererRef
            above) so drawn shapes always stack above census/radius shapes,
            deterministically — see DRAWN_PANE_NAME comment. */}
        <Pane name={DRAWN_PANE_NAME} style={{ zIndex: DRAWN_PANE_Z_INDEX }} />
        {/* key on the basemap so the tile layer swaps cleanly when it changes. */}
        <TileLayer key={basemapKey} url={tiles.url} attribution={tiles.attribution} />

        {circles.map((c) => (
          <Circle
            key={c.key}
            center={c.center}
            radius={c.radius}
            pathOptions={c.style}
            eventHandlers={getShapeClickHandlers(c.groupId, c.key)}
          />
        ))}

        {visibleGeojsonLayers.map((g) => (
          <GeoJSON
            key={g.key}
            data={g.geometry}
            style={() => g.style}
            // 'drawn' shapes stay on the shared SVG renderer (see header
            // comment + drawnRendererRef above); everything else inherits the
            // MapContainer's preferCanvas default.
            renderer={g.isDrawn ? drawnRendererRef.current : undefined}
            ref={(instance) => {
              if (instance) geoLayerRefsRef.current.set(g.key, instance);
              else geoLayerRefsRef.current.delete(g.key);
            }}
            eventHandlers={getShapeClickHandlers(g.groupId, g.key)}
          />
        ))}

        {/* Selection halo — a non-interactive dashed outline in the app's
            interactive color (not the group's own color), so "selected" reads
            the same regardless of which group/color owns the shape. Hidden
            while reshaping: Geoman's own vertex handles are the selection cue
            at that point. */}
        {selectedInfo && !reshaping && (() => {
          const { area } = selectedInfo;
          const haloStyle = {
            fill: false,
            color: 'var(--color-interactive)',
            weight: 2,
            dashArray: '6 5',
            interactive: false,
          };
          if (area.area_type === 'radius' && area.params?.center && area.params?.radius_mi) {
            return (
              <Circle
                center={area.params.center}
                radius={area.params.radius_mi * MILES_TO_METERS * 1.03}
                pathOptions={haloStyle}
              />
            );
          }
          if (area.geometry && area.geometry.type) {
            return <GeoJSON data={area.geometry} style={() => haloStyle} />;
          }
          return null;
        })()}

        <FitBounds bounds={bounds} />
        {/* Geoman draw integration (polygon/rectangle) + precise-radius click
            capture — publishes triggers into controlsRef, reports finished
            polygons via onShapeDrawn and radius center clicks via onRadiusCenter. */}
        <DrawController
          controlsRef={controlsRef}
          onShapeDrawn={onShapeDrawn}
          onRadiusCenter={handleRadiusCenter}
        />
        {/* On-canvas size/population pills, projected + tracked to their shapes. */}
        <ShapeLabels items={labelItems} />
        {/* Deselects on a background click; suppressed while a popup/reshape
            needs its own explicit exit (see interactionLocked above). */}
        <SelectionController
          suppressed={interactionLocked}
          onDeselect={() => onSelectAreaRef.current?.(null)}
        />
        {/* Click-to-select action popup / "Done reshaping" button. */}
        <SelectionOverlay popup={overlayPopup} reshaping={overlayReshaping} onDoneReshaping={finishReshape} />
      </MapContainer>

      {/* Precise-radius entry popup — rendered as a sibling of the map (not a
          Leaflet layer) so it overlays the canvas at the click's pixel point. */}
      {pendingRadius && (
        <RadiusEntryPopup
          point={pendingRadius.point}
          defaultMiles={DEFAULT_RADIUS_MI}
          onAdd={confirmRadius}
          onCancel={cancelRadius}
        />
      )}

      {/* Radius-edit popup — same component, different copy/callback (see
          startEditRadius); also rendered outside the Leaflet container. */}
      {editingRadius && (
        <RadiusEntryPopup
          point={editingRadius.point}
          defaultMiles={editingRadius.currentMiles}
          heading="Edit radius"
          submitLabel="Save"
          onAdd={confirmEditRadius}
          onCancel={cancelEditRadius}
        />
      )}

      {/* Progressive-reveal progress — see the revealedKeysRef effect above.
          Rendered as a sibling (not a Leaflet layer): shapes are still
          appearing on the canvas underneath as this counts up. */}
      {renderProgress && (
        <div className="shape-render-progress" role="status" aria-live="polite">
          <span>
            Rendering {formatNumber(renderProgress.done)} of {formatNumber(renderProgress.total)} shapes…
          </span>
          <div className="shape-render-progress-track">
            <div
              className="shape-render-progress-fill"
              style={{ width: `${Math.round((renderProgress.done / renderProgress.total) * 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
});

export default MapCanvas;

/**
 * Walk a GeoJSON geometry/feature and push all coordinate pairs (as [lat,lon])
 * into `acc` for bounds computation. Handles Point/LineString/Polygon/Multi*
 * and Feature/FeatureCollection wrappers. GeoJSON coords are [lon,lat]; we
 * flip to Leaflet's [lat,lon].
 */
function collectGeoJSONBounds(node, acc) {
  if (!node) return;
  if (node.type === 'FeatureCollection') {
    (node.features || []).forEach((f) => collectGeoJSONBounds(f, acc));
    return;
  }
  if (node.type === 'Feature') {
    collectGeoJSONBounds(node.geometry, acc);
    return;
  }
  const coords = node.coordinates;
  if (!coords) return;

  const pushPair = (pair) => {
    if (Array.isArray(pair) && typeof pair[0] === 'number' && typeof pair[1] === 'number') {
      acc.push([pair[1], pair[0]]);
    }
  };
  const walk = (arr) => {
    if (!Array.isArray(arr)) return;
    if (typeof arr[0] === 'number') {
      pushPair(arr);
    } else {
      arr.forEach(walk);
    }
  };
  walk(coords);
}
